import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Vector from "../../assets/login-page-vector.svg";
import axiosInstance from "../../axiosInstance";
import { trackEvent } from "../../helperFunctions";
import URL from "../../urls";
import { loginURL } from "../../variable";
import { Button } from "../common/Buttons";
import FlexBox from "../common/FlexBox";
import { Body1, ButtonText, Display, Support } from "../common/Headings";
import Input from "../common/InputBox";
import {
  GREEN_200,
  MOSS_GREEN_800,
  MOSS_GREEN_900,
  Red,
  white,
} from "../common/colors";
import Bugsnag from "@bugsnag/js";

const Wrapper = styled(FlexBox)`
  align-items: center;
  height: 100vh;
  overflow: hidden;
  background-color: ${GREEN_200};
`;

const Container = styled(FlexBox)`
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  width: 45rem;
  margin: auto;
  padding: 4rem 6.5rem 0;
  justify-content: space-between;
  background-color: ${white};

  @media screen and (max-width: 768px) {
    padding: 4rem 1.5rem 0;
  }
`;

const Header = styled(FlexBox)`
  flex-direction: column;
  row-gap: 1rem;
  align-items: center;
`;

const ErrorContainer = styled.div`
  max-width: 26rem;
  margin: auto;
  text-align: center;
`;

const Logo = styled.img`
  width: 100%;
  max-width: 16.75rem;
  height: 4rem;
  flex-shrink: 0;
`;

const Img = styled.img`
  width: 67%;
  max-width: 30rem;
`;

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleEmail = value => {
    var filter =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (value.length > 2 && !filter.test(value)) {
      setEmailError("Invalid Email");
    } else {
      setEmailError("");
    }
    setEmail(value);
  };

  const sendPwdResetRequest = () => {
    setLoading(true);
    setError(false);
    setSuccess(false);
    setErrorMsg("");
    setSuccessMsg("");

    axiosInstance
      .post(URL.passwordReset, {
        email: email,
        redirect_url: loginURL,
      })
      .then(() => {
        setLoading(false);
        setSuccess(true);
        setSuccessMsg(
          `An email has been sent to '${email}' containing instructions for resetting your password.`
        );
        trackEvent({
          event: "twa_reset_pswd_click",
          payload: { auth_status: "success" },
        });
      })
      .catch(error => {
        Bugsnag.notify(error);
        setLoading(false);
        setError(true);
        setErrorMsg(
          "Error sending password reset email. Check your email ID and try again."
        );
        trackEvent({
          event: "twa_reset_pswd_click",
          payload: { auth_status: "failure" },
        });
      });
  };

  return (
    <Wrapper>
      <Container>
        <FlexBox column rowGap="2.5rem" align="center">
          <Logo
            src={
              "https://assets.theinnerhour.com/compressed/Logo_1776245326790_9yz69o9.svg"
            }
            draggable={false}
            alt="amaha professional logo"
          />
          <Header>
            <Display bold>Reset Password</Display>
            <Body1 textAlign="center">
              If your email address is registered with us, you will receive a
              link to reset your password via the registered email address.
            </Body1>
          </Header>
          <FlexBox width="100%" column rowGap="1rem">
            <Input
              name="email"
              placeholder="Email Address"
              value={email}
              onChange={value => handleEmail(value)}
              onSubmit={sendPwdResetRequest}
              error={emailError}
            />
            <ErrorContainer>
              {success && (
                <Support bold color={MOSS_GREEN_900} textAlign="center">
                  {successMsg}
                </Support>
              )}
              {error && (
                <Support bold color={Red} textAlign="center">
                  {errorMsg}
                </Support>
              )}
            </ErrorContainer>
          </FlexBox>
          <Button
            onClick={sendPwdResetRequest}
            disabled={email.length < 3 || emailError || loading}
          >
            {loading ? "Loading" : "Reset password"}
          </Button>
          <ButtonText
            bold
            onClick={() => navigate(`/login`)}
            color={MOSS_GREEN_800}
          >
            BACK TO LOGIN
          </ButtonText>
        </FlexBox>
        <Img src={Vector} alt="banner" />
      </Container>
    </Wrapper>
  );
};

export default ForgotPassword;
